import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Send, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOW_HEARD_OPTIONS = [
  { value: 'google', label: 'Google Search' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Friend/Family Referral' },
  { value: 'veteran-org', label: 'Veteran Organization' },
  { value: 'job-board', label: 'Job Board' },
  { value: 'military-transition', label: 'Military Transition Program' },
  { value: 'employer-referral', label: 'Employer Referral' },
  { value: 'networking-event', label: 'Networking Event' },
  { value: 'other', label: 'Other' },
];

const LeadGenerator = () => {
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', hasResume: '', howDidYouHear: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleLeadChange = (e) => {
    const { name, value } = e.target;
    setLeadForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setResumeFile(file);
  };

  const handleRemoveFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    setLeadError('');

    try {
      // If resume is attached, use the resume-upload endpoint (sends email WITH attachment)
      if (resumeFile) {
        const formData = new FormData();
        formData.append('name', leadForm.name);
        formData.append('email', leadForm.email);
        formData.append('phone', leadForm.phone || '');
        formData.append('resume', resumeFile);

        await axios.post(`${API}/leads/resume-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // No resume — just submit lead info
        await axios.post(`${API}/leads/resume-score`, leadForm);
      }

      setLeadSuccess(true);
    } catch (error) {
      setLeadError('Something went wrong. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-[#0a1628]" data-testid="lead-generator-section">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="text-white">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-6">
                <FileText className="text-[#0a1628]" size={32} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What's your resume score?</h2>
              <p className="text-xl text-gray-300 mb-6">
                Find out how your resume stacks up and what to fix to get more interviews.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="text-[#d4a574]" size={20} />
                  <span>Free personalized feedback</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="text-[#d4a574]" size={20} />
                  <span>Tips to improve your chances</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="text-[#d4a574]" size={20} />
                  <span>No obligation, just value</span>
                </li>
              </ul>
            </div>

            {/* Right - Form */}
            <Card className="border-2 border-[#d4a574]">
              <CardContent className="p-8">
                {leadSuccess ? (
                  <div className="text-center py-8" data-testid="lead-success">
                    <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
                    <h3 className="text-2xl font-bold text-[#0a1628] mb-2">
                      {resumeFile ? 'Resume Submitted!' : 'Thank You!'}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {resumeFile
                        ? "We've received your resume and will reach out with personalized feedback and opportunities."
                        : "We'll review your information and reach out with personalized feedback soon."}
                    </p>
                    <div className="border-t pt-6 mt-4">
                      <p className="text-sm text-gray-500 mb-4">Want to tell us more about your background?</p>
                      <Link to="/contact?type=resume">
                        <Button className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                          Complete Full Profile <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="lead-form">
                    <div>
                      <Label htmlFor="lead-name" className="text-[#0a1628] font-semibold">Name *</Label>
                      <Input id="lead-name" name="name" type="text" required value={leadForm.name} onChange={handleLeadChange} className="mt-1" placeholder="Your full name" />
                    </div>
                    <div>
                      <Label htmlFor="lead-email" className="text-[#0a1628] font-semibold">Email *</Label>
                      <Input id="lead-email" name="email" type="email" required value={leadForm.email} onChange={handleLeadChange} className="mt-1" placeholder="your@email.com" />
                    </div>
                    <div>
                      <Label htmlFor="lead-phone" className="text-[#0a1628] font-semibold">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Input id="lead-phone" name="phone" type="tel" value={leadForm.phone} onChange={handleLeadChange} className="mt-1" placeholder="(555) 123-4567" />
                    </div>
                    <div>
                      <Label className="text-[#0a1628] font-semibold">Do you currently have a resume?</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="radio" name="hasResume" value="yes" checked={leadForm.hasResume === 'yes'} onChange={handleLeadChange} className="w-4 h-4 text-[#d4a574]" />
                          <span className="text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="radio" name="hasResume" value="no" checked={leadForm.hasResume === 'no'} onChange={handleLeadChange} className="w-4 h-4 text-[#d4a574]" />
                          <span className="text-gray-700">No</span>
                        </label>
                      </div>
                    </div>

                    {/* Inline resume upload — appears when "Yes" is selected */}
                    {leadForm.hasResume === 'yes' && (
                      <div data-testid="lead-resume-upload-section">
                        <Label className="text-[#0a1628] font-semibold">Upload Your Resume</Label>
                        {resumeFile ? (
                          <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center space-x-2">
                              <FileText className="text-green-600" size={20} />
                              <span className="text-sm text-green-800 font-medium">{resumeFile.name}</span>
                            </div>
                            <button type="button" onClick={handleRemoveFile} className="text-gray-400 hover:text-red-500">
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            className="mt-2 border-2 border-dashed border-[#d4a574] rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors text-center"
                            data-testid="lead-resume-dropzone"
                          >
                            <Upload className="mx-auto text-[#d4a574] mb-1" size={24} />
                            <p className="text-sm text-gray-600">Click to select your resume (PDF, DOC, DOCX)</p>
                          </div>
                        )}
                        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="lead-howDidYouHear" className="text-[#0a1628] font-semibold">How did you hear about us?</Label>
                      <select
                        id="lead-howDidYouHear"
                        name="howDidYouHear"
                        value={leadForm.howDidYouHear}
                        onChange={handleLeadChange}
                        data-testid="lead-how-did-you-hear"
                        className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                      >
                        <option value="">Select an option</option>
                        {HOW_HEARD_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {leadError && <p className="text-red-500 text-sm">{leadError}</p>}

                    <Button type="submit" disabled={leadSubmitting} className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] text-lg py-6" data-testid="lead-submit-btn">
                      {leadSubmitting ? 'Submitting...' : (
                        <>
                          {resumeFile ? 'Submit with Resume' : 'Get My Score'}
                          <Send className="ml-2" size={20} />
                        </>
                      )}
                    </Button>

                    <p className="text-center text-sm text-gray-500">No resume? No problem. We'll help you start from scratch.</p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadGenerator;
